<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <Form class="form">
            <h4 class="Paragraph-Medium text-left">Select grade and subjects</h4>
            <div class="w-full">
                <div v-for="(level, index) in teachingLevels" :key="index" class="mb-4">
                    <!-- Grade + Subjects row -->
                    <div class="grid">
                        <div class="col-12 md:col-4 pr-3">
                            <div class="flex flex-column gap-2 pt-2 relative mb-5">
                                <label class="Label align-self-start">Grade :</label>
                                <Select
                                    v-model="level.grade"
                                    size="small"
                                    variant="filled"
                                    :options="gradeOptions"
                                    :loading="isLoadingGrades"
                                    optionLabel="name"
                                    optionValue="code"
                                    placeholder="Select grade"
                                    class="w-full"
                                    show-clear
                                    @change="onGradeChange(index)"
                                    :optionDisabled="(option) => isGradeDisabled(option.code, index)"
                                />
                                <small v-if="errors[`grade_${index}`]" class="error-text">
                                    {{ errors[`grade_${index}`] }}
                                </small>
                            </div>
                        </div>

                        <div class="col-12 md:col-8 pr-3">
                            <div class="flex flex-column gap-2 pt-2 relative mb-5">
                                <div class="flex justify-content-between align-items-center">
                                    <label class="Label">Subjects :</label>
                                    <span
                                        class="remove-grade-btn"
                                        :style="{ visibility: teachingLevels.length > 1 ? 'visible' : 'hidden' }"
                                        @click="removeGrade(index)"
                                    >Remove</span>
                                </div>
                                <MultiSelect
                                    v-model="level.subjects"
                                    size="small"
                                    variant="filled"
                                    :options="getAvailableSubjects(level.grade)"
                                    optionLabel="name"
                                    optionValue="code"
                                    placeholder="Select subjects"
                                    class="w-full"
                                    show-clear
                                    @change="() => onSubjectsChange(index)"
                                    display="chip"
                                    :optionDisabled="(option) => isSubjectDisabled(option, index)"
                                    :disabled="!level.grade || getAvailableSubjects(level.grade).length === 0"
                                    :maxSelectedLabels="3"
                                />
                                <small
                                    :class="errors[`subjects_${index}`] ? 'error-text' : 'warning-text'"
                                    :style="{ visibility: errors[`subjects_${index}`] || warnings[`subjects_${index}`] ? 'visible' : 'hidden' }"
                                >{{ errors[`subjects_${index}`] || warnings[`subjects_${index}`] || '' }}</small>
                            </div>
                        </div>
                    </div>

                    <!-- Per-subject class pricing -->
                    <div v-if="level.subjects && level.subjects.length > 0" class="pricing-section ml-1 mr-1 mt-1 mb-2">
                        <!-- Column headers (desktop only) -->
                        <div class="grid align-items-center pricing-header py-1 desktop-header">
                            <div class="col-3">
                                <span class="pricing-col-label">Subject</span>
                            </div>
                            <div class="col-4 pl-2">
                                <span class="pricing-col-label">Individual Class / Hourly Rate</span>
                            </div>
                            <div class="col-4 pl-2">
                                <span class="pricing-col-label">Group Class / Hourly Rate</span>
                            </div>
                            <div class="col-1"></div>
                        </div>

                        <template v-for="subjectCode in level.subjects" :key="subjectCode">
                            <div class="grid align-items-center subject-pricing-row pt-1">
                                <!-- Subject name (mobile: col-10, desktop: col-3) -->
                                <div class="col-10 md:col-3 flex align-items-center">
                                    <span class="subject-label">{{ getSubjectName(level.grade, subjectCode) }}</span>
                                </div>

                                <!-- Remove button (mobile only) -->
                                <div class="col-2 flex justify-content-end align-items-center mobile-remove">
                                    <button
                                        type="button"
                                        class="remove-subject-btn"
                                        title="Remove subject"
                                        @click="removeSubject(index, subjectCode)"
                                    ><i class="pi pi-times"></i></button>
                                </div>

                                <!-- Individual class -->
                                <div class="col-12 md:col-4 pl-2">
                                    <span class="mobile-col-label">Individual Class / Session</span>
                                    <div class="flex align-items-center gap-1 w-full">
                                        <Checkbox
                                            v-model="level.subjectPricing[subjectCode].individualClass.available"
                                            :inputId="`ind_${index}_${subjectCode}`"
                                            binary
                                            class="teal-checkbox flex-shrink-0"
                                            @change="onClassTypeChange(index, subjectCode)"
                                        />
                                        <span class="rs-label flex-shrink-0">Rs.</span>
                                        <InputNumber
                                            v-model="level.subjectPricing[subjectCode].individualClass.charge"
                                            :disabled="!level.subjectPricing[subjectCode].individualClass.available"
                                            mode="decimal"
                                            :minFractionDigits="2"
                                            :maxFractionDigits="2"
                                            :useGrouping="true"
                                            size="small"
                                            variant="filled"
                                            class="flex-1"
                                            style="min-width:0"
                                            inputClass="w-full"
                                            placeholder="0"
                                            @input="clearFeeError(index, subjectCode, 'ind')"
                                        />
                                    </div>
                                </div>

                                <!-- Group class -->
                                <div class="col-12 md:col-4 pl-2">
                                    <span class="mobile-col-label">Group Class / Session</span>
                                    <div class="flex align-items-center gap-1 w-full">
                                        <Checkbox
                                            v-model="level.subjectPricing[subjectCode].groupClass.available"
                                            :inputId="`grp_${index}_${subjectCode}`"
                                            binary
                                            class="teal-checkbox flex-shrink-0"
                                            @change="onClassTypeChange(index, subjectCode)"
                                        />
                                        <span class="rs-label flex-shrink-0">Rs.</span>
                                        <InputNumber
                                            v-model="level.subjectPricing[subjectCode].groupClass.charge"
                                            :disabled="!level.subjectPricing[subjectCode].groupClass.available"
                                            mode="decimal"
                                            :minFractionDigits="2"
                                            :maxFractionDigits="2"
                                            :useGrouping="true"
                                            size="small"
                                            variant="filled"
                                            class="flex-1"
                                            style="min-width:0"
                                            inputClass="w-full"
                                            placeholder="0"
                                            @input="clearFeeError(index, subjectCode, 'grp')"
                                        />
                                    </div>
                                </div>

                                <!-- Remove subject (desktop only) -->
                                <div class="md:col-1 flex justify-content-end align-items-center desktop-remove">
                                    <button
                                        type="button"
                                        class="remove-subject-btn"
                                        title="Remove subject"
                                        @click="removeSubject(index, subjectCode)"
                                    ><i class="pi pi-times"></i></button>
                                </div>

                                <!-- Row-level message -->
                                <div class="col-12 md:col-9 md:col-offset-3 subject-msg-row">
                                    <small
                                        :class="getSubjectMessage(index, subjectCode)?.type === 'error' ? 'msg-error' : 'msg-warning'"
                                        :style="{ visibility: getSubjectMessage(index, subjectCode) ? 'visible' : 'hidden' }"
                                    >{{ getSubjectMessage(index, subjectCode)?.text ?? '' }}</small>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <div class="flex justify-content-start pl-2 mb-4 pt-2">
                    <Button label="Add a grade" class="teal-button mt-2" @click="addGrade" />
                </div>

                <Divider class="w-full" />
            </div>

            <div class="col-12 md:col-6 pr-3">
                <div class="flex flex-column gap-3 pt-4 relative mb-5">
                    <label for="occupation" class="Label align-self-start">I'm a : </label>
                    <Select v-model="selectedoccupation" :options="occupation" variant="filled" size="small"
                        optionLabel="value" placeholder="Select occupation type" class="w-full" show-clear />
                    <small v-if="errors.occupation" class="error-text">{{ errors.occupation }}</small>
                </div>
            </div>

            <div class="grid w-full">
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4 relative mb-5">
                        <label for="subjects" class="Label align-self-start">I do classes:</label>
                        <Select v-model="selectedClassType" :options="classType" variant="filled" size="small"
                            optionLabel="value" placeholder="Select class type" class="w-full" show-clear />
                        <small v-if="errors.classType" class="error-text">{{ errors.classType }}</small>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4 justify-content-center align-items-center lg:px-8 relative mb-5">
                        <RadioButtonGroup v-model="selectedLocation" name="location" class="flex flex-column gap-2">
                            <div v-for="item in locations" :key="item.key" class="flex items-center gap-2">
                                <RadioButton size="small" :inputId="item.key" :value="item.key"
                                    class="teal-radio-button" />
                                <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                            </div>
                        </RadioButtonGroup>
                        <small v-if="errors.location" class="error-text" style="left: 0; padding-left: 5rem;">{{
                            errors.location }}</small>
                    </div>
                </div>
                <div class="col-12 md:col-6 p-0">
                    <div class="flex flex-column gap-3 pt-4 relative mb-5">
                        <label for="occupation" class="Label align-self-start">My teaching language/s:</label>
                        <div class="flex justify-content-center">
                            <CheckboxGroup v-model="selectedLanguage" name="item" class="flex flex-column gap-2">
                                <div v-for="item in languages" :key="item.key" class="flex items-center gap-2">
                                    <Checkbox :inputId="item.key" :value="item.key" class="teal-checkbox" />
                                    <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                                </div>
                            </CheckboxGroup>
                        </div>
                        <small v-if="errors.language" class="error-text">{{ errors.language }}</small>
                    </div>
                    <div class="flex flex-column gap-3 pt-4 relative mb-5">
                        <label class="Label align-self-start">I'm available in:</label>
                        <div class="flex justify-content-center">
                            <CheckboxGroup v-model="selectedAvailability" name="availability"
                                class="flex flex-column gap-2">
                                <div v-for="item in availabilityOptions" :key="item.key"
                                    class="flex items-center gap-2">
                                    <Checkbox :inputId="item.key" :value="item.key" class="teal-checkbox" />
                                    <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                                </div>
                            </CheckboxGroup>
                        </div>
                        <small v-if="errors.availability" class="error-text">{{ errors.availability }}</small>
                    </div>
                </div>
            </div>

            <div class="col-12 col-6 flex justify-content-between p-0">
                <div class="align-items-center gap-3">
                    <Button label="Back" class="teal-button-transparent mt-6" @click="Cancel" />
                </div>
                <div class="align-items-center gap-3">
                    <Button label="Next" class="teal-button mt-6" @click="Next" />
                </div>
            </div>
        </Form>
    </div>
</template>

<script setup>
import { ref, defineEmits, watch, onMounted } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import RadioButtonGroup from 'primevue/radiobuttongroup';
import Checkbox from 'primevue/checkbox';
import CheckboxGroup from 'primevue/checkboxgroup';
import InputNumber from 'primevue/inputnumber';
import Divider from 'primevue/divider';
import Select from "primevue/select";
import { useAuthStore } from "@/stores/authStore";
import { occupation, classType, locations, languages, availabilityOptions, gradeOptions, isLoadingGrades, fetchGradeOptions } from "@/utils/options";
import { getLimits } from "@/api/tutor";


const emit = defineEmits(["submit-teaching-info", "previous-step"]);

const errors = ref({});

const initSubjectPricing = () => ({
    individualClass: { available: false, charge: null },
    groupClass: { available: false, charge: null }
});

const teachingLevels = ref([
    { grade: null, subjects: [], subjectPricing: {} }
]);

const selectedoccupation = ref();
const selectedClassType = ref(classType.length === 1 ? classType[0] : null);
const selectedLocation = ref(null);
const selectedLanguage = ref([]);
const selectedAvailability = ref([]);

const authStore = useAuthStore();
const subjectLimit = ref(null);
const minSessionCharge = ref(30);
const warnings = ref({});

onMounted(async () => {
    const [, limitsRes] = await Promise.allSettled([
        fetchGradeOptions(),
        getLimits(),
    ]);
    if (limitsRes.status === 'fulfilled') {
        const limits = limitsRes.value?.data?.data?.limits
        subjectLimit.value = limits?.listedSubjects ?? null;
        minSessionCharge.value = limits?.minSessionCharge ?? 30;
    }

    const user = authStore.user;
    if (user) {
        // Pre-fill from new flat format: group entries by grade
        if (user.teachingLevels && user.teachingLevels.length > 0) {
            const grouped = {};
            user.teachingLevels.forEach(entry => {
                if (!grouped[entry.grade]) {
                    grouped[entry.grade] = { grade: entry.grade, subjects: [], subjectPricing: {} };
                }
                grouped[entry.grade].subjects.push(entry.subject);
                grouped[entry.grade].subjectPricing[entry.subject] = {
                    individualClass: entry.individualClass || { available: false, charge: null },
                    groupClass: entry.groupClass || { available: false, charge: null }
                };
            });
            teachingLevels.value = Object.values(grouped);
        }

        if (user.tutorType) {
            selectedoccupation.value = occupation.find(o => o.key === user.tutorType);
        }
        if (user.classMode) {
            selectedClassType.value = classType.find(c => c.key === user.classMode);
        }
        selectedLocation.value = user.classLocation || null;
        selectedLanguage.value = user.teachingLanguage || [];
        selectedAvailability.value = user.teachingAvailability || [];
    }
});

// HELPERS

const getAvailableSubjects = (gradeCode) => {
    if (!gradeCode) return [];
    const gradeObj = gradeOptions.value.find(g => g.code === gradeCode);
    return gradeObj && gradeObj.subjects ? gradeObj.subjects : [];
};

const getSubjectName = (gradeCode, subjectCode) => {
    const sub = getAvailableSubjects(gradeCode).find(s => s.code === subjectCode);
    return sub ? sub.name : subjectCode;
};

const onGradeChange = (index) => {
    errors.value[`grade_${index}`] = null;
    warnings.value[`subjects_${index}`] = null;
    teachingLevels.value[index].subjects = [];
    teachingLevels.value[index].subjectPricing = {};
};

const isGradeDisabled = (gradeCode, currentIndex) =>
    teachingLevels.value.some((l, i) => i !== currentIndex && l.grade === gradeCode);

const removeGrade = (index) => {
    teachingLevels.value.splice(index, 1);
    Object.keys(errors.value)
        .filter(k => k.startsWith('grade_') || k.startsWith('subjects_') ||
            k.startsWith('classFormat_') || k.startsWith('indFee_') || k.startsWith('grpFee_'))
        .forEach(k => delete errors.value[k]);
    Object.keys(warnings.value).forEach(k => delete warnings.value[k]);
};

const isSubjectDisabled = (option, levelIndex) => {
    if (!subjectLimit.value || subjectLimit.value.max === null) return false;
    const level = teachingLevels.value[levelIndex];
    return level.subjects.length >= subjectLimit.value.max && !level.subjects.includes(option.code);
};

const onSubjectsChange = (index) => {
    errors.value[`subjects_${index}`] = null;
    warnings.value[`subjects_${index}`] = null;

    const level = teachingLevels.value[index];

    // Init pricing for newly added subjects; remove for deselected
    level.subjects.forEach(code => {
        if (!level.subjectPricing[code]) level.subjectPricing[code] = initSubjectPricing();
    });
    Object.keys(level.subjectPricing).forEach(code => {
        if (!level.subjects.includes(code)) delete level.subjectPricing[code];
    });

    if (!subjectLimit.value || subjectLimit.value.max === null) return;

    const max = subjectLimit.value.max;

    // Trim if over per-grade limit
    if (level.subjects.length > max) {
        level.subjects = level.subjects.slice(0, max);
        Object.keys(level.subjectPricing).forEach(code => {
            if (!level.subjects.includes(code)) delete level.subjectPricing[code];
        });
    }

    if (level.subjects.length >= max) {
        warnings.value[`subjects_${index}`] = `Subject listing limit reached (${level.subjects.length}/${max})`;
    }
};

const addGrade = () => {
    teachingLevels.value.push({ grade: null, subjects: [], subjectPricing: {} });
};

const onClassTypeChange = (levelIndex, subjectCode) => {
    delete errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    const pricing = teachingLevels.value[levelIndex].subjectPricing[subjectCode];
    if (!pricing.individualClass.available) pricing.individualClass.charge = null;
    if (!pricing.groupClass.available) pricing.groupClass.charge = null;
};

const clearFeeError = (levelIndex, subjectCode, type) => {
    if (type === 'ind') delete errors.value[`indFee_${levelIndex}_${subjectCode}`];
    if (type === 'grp') delete errors.value[`grpFee_${levelIndex}_${subjectCode}`];
};

const removeSubject = (levelIndex, subjectCode) => {
    const level = teachingLevels.value[levelIndex];
    level.subjects = level.subjects.filter(s => s !== subjectCode);
    delete level.subjectPricing[subjectCode];
    delete errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    delete errors.value[`indFee_${levelIndex}_${subjectCode}`];
    delete errors.value[`grpFee_${levelIndex}_${subjectCode}`];

    if (subjectLimit.value?.max !== null) {
        if (level.subjects.length < subjectLimit.value.max) {
            warnings.value[`subjects_${levelIndex}`] = null;
        }
    }
};

const getSubjectMessage = (levelIndex, subjectCode) => {
    const pricing = teachingLevels.value[levelIndex]?.subjectPricing?.[subjectCode];
    if (!pricing) return null;

    // Post-submit red errors take priority
    const classFormatError = errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    const indFeeError = errors.value[`indFee_${levelIndex}_${subjectCode}`];
    const grpFeeError = errors.value[`grpFee_${levelIndex}_${subjectCode}`];
    if (classFormatError) return { text: classFormatError, type: 'error' };
    if (indFeeError) return { text: indFeeError, type: 'error' };
    if (grpFeeError) return { text: grpFeeError, type: 'error' };

    // Yellow guidance warnings (pre-submit)
    const hasIndividual = pricing.individualClass?.available;
    const hasGroup = pricing.groupClass?.available;
    if (!hasIndividual && !hasGroup) {
        return { text: "Select at least one class type and enter a price per session", type: 'warning' };
    }
    if (hasIndividual && !pricing.individualClass.charge) {
        return { text: "Enter a price per session for individual class", type: 'warning' };
    }
    if (hasIndividual && pricing.individualClass.charge < minSessionCharge.value) {
        return { text: `Minimum hourly rate is Rs. ${minSessionCharge.value} for individual class`, type: 'error' };
    }
    if (hasGroup && !pricing.groupClass.charge) {
        return { text: "Enter a price per session for group class", type: 'warning' };
    }
    if (hasGroup && pricing.groupClass.charge < minSessionCharge.value) {
        return { text: `Minimum hourly rate is Rs. ${minSessionCharge.value} for group class`, type: 'error' };
    }
    return null;
};

// --- WATCHERS TO CLEAR ERRORS ON INPUT ---
watch(selectedoccupation, (val) => { if (val) errors.value.occupation = null; });
watch(selectedClassType, (val) => { if (val) errors.value.classType = null; });
watch(selectedLocation, (val) => { if (val) errors.value.location = null; });
watch(selectedLanguage, (val) => { if (val && val.length > 0) errors.value.language = null; }, { deep: true });
watch(selectedAvailability, (val) => { if (val && val.length > 0) errors.value.availability = null; }, { deep: true });

const Cancel = () => {
    emit("previous-step");
};

const validateForm = () => {
    errors.value = {};

    teachingLevels.value.forEach((level, index) => {
        if (!level.grade) {
            errors.value[`grade_${index}`] = "Grade is required";
        }
        if (!level.subjects || level.subjects.length === 0) {
            errors.value[`subjects_${index}`] = "At least one subject is required";
        }

        level.subjects.forEach(subjectCode => {
            const pricing = level.subjectPricing[subjectCode];
            if (!pricing) return;

            const hasIndividual = pricing.individualClass.available;
            const hasGroup = pricing.groupClass.available;

            if (!hasIndividual && !hasGroup) {
                errors.value[`classFormat_${index}_${subjectCode}`] = "Select at least one class type";
            }
            if (hasIndividual && !pricing.individualClass.charge) {
                errors.value[`indFee_${index}_${subjectCode}`] = "Enter a valid fee for individual class";
            } else if (hasIndividual && pricing.individualClass.charge < minSessionCharge.value) {
                errors.value[`indFee_${index}_${subjectCode}`] = `Minimum hourly rate is Rs. ${minSessionCharge.value} for individual class`;
            }
            if (hasGroup && !pricing.groupClass.charge) {
                errors.value[`grpFee_${index}_${subjectCode}`] = "Enter a valid fee for group class";
            } else if (hasGroup && pricing.groupClass.charge < minSessionCharge.value) {
                errors.value[`grpFee_${index}_${subjectCode}`] = `Minimum hourly rate is Rs. ${minSessionCharge.value} for group class`;
            }
        });
    });

    if (!selectedoccupation.value) errors.value.occupation = "Occupation is required";
    if (!selectedClassType.value) errors.value.classType = "Class type is required";
    if (selectedClassType.value?.key !== 'online' && !selectedLocation.value) {
        errors.value.location = "Class location is required";
    }
    if (!selectedLanguage.value || selectedLanguage.value.length === 0) errors.value.language = "At least one language is required";
    if (!selectedAvailability.value || selectedAvailability.value.length === 0) errors.value.availability = "At least one availability slot is required";

    return Object.keys(errors.value).length === 0;
};

const Next = async () => {
    if (selectedLocation.value === null) selectedLocation.value = 'both';
    if (!validateForm()) return;

    // Build flat array: one entry per grade+subject combo
    const flatTeachingLevels = [];
    teachingLevels.value.forEach(level => {
        level.subjects.forEach(subjectCode => {
            const pricing = level.subjectPricing[subjectCode] || {};
            flatTeachingLevels.push({
                grade: level.grade,
                subject: subjectCode,
                individualClass: pricing.individualClass || { available: false, charge: null },
                groupClass: pricing.groupClass || { available: false, charge: null }
            });
        });
    });

    const payload = {
        teachingLevels: flatTeachingLevels,
        tutorType: selectedoccupation.value.key,
        classMode: selectedClassType.value.key,
        classLocation: selectedLocation.value,
        teachingLanguage: selectedLanguage.value,
        teachingAvailability: selectedAvailability.value,
    };
    emit("submit-teaching-info", payload);
};
</script>

<style scoped>
.form {
    width: 100%;
    min-width: 60vw;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}

.remove-grade-btn {
    font-size: 0.8rem;
    color: #acacac;
    cursor: pointer;
    line-height: 1;
}

.remove-grade-btn:hover {
    color: #dc2626;
}

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.7rem;
    margin-bottom: -0.7rem;
    left: 0;
    padding-left: 0.25rem;
}

.warning-text {
    color: #d97706;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.7rem;
    margin-bottom: -0.7rem;
    left: 0;
    padding-left: 0.25rem;
}

.pricing-section {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    border-radius: 0 0.25rem 0.25rem 0;
    background: var(--p-surface-50, #f8fafc);
    border-radius: 0.375rem;
}

.pricing-header {
    border-bottom: 1px solid var(--p-surface-border, #e2e8f0);
    margin-bottom: 0.25rem;
}

.pricing-col-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--p-text-muted-color, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.subject-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--p-text-color, #1e293b);
}

.subject-pricing-row:not(:last-child) {
    border-bottom: 1px solid var(--p-surface-border, #f1f5f9);
}

.remove-subject-btn {
    background: none;
    border: none;
    color: #acacac;
    font-size: 0.75rem;
    line-height: 1;
    cursor: pointer;
    padding: 0.3rem;
    border-radius: 0.25rem;
    transition: color 0.15s, background 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.remove-subject-btn:hover {
    color: #dc2626;
    background: #fee2e2;
}

.subject-msg-row {
    height: 1rem;
    overflow: hidden;
    padding: 0;
    display: flex;
    align-items: center;
}

.msg-warning {
    color: #d97706;
    font-size: 0.72rem;
}

.msg-error {
    color: #dc2626;
    font-size: 0.72rem;
}

.mobile-col-label {
    display: none;
}

@media (max-width: 767px) {
    .desktop-header {
        display: none !important;
    }

    .desktop-remove {
        display: none !important;
    }

    .mobile-col-label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        color: var(--p-text-muted-color, #64748b);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.2rem;
    }
}

@media (min-width: 768px) {
    .mobile-remove {
        display: none !important;
    }
}

.rs-label {
    font-size: 0.8rem;
    color: var(--p-text-muted-color, #64748b);
}
</style>

